import { supabase } from '../lib/supabase';

/**
 * Storage Service for handling file uploads to Supabase Storage
 */

const BUCKET_NAME = 'learning-trail-images';

/**
 * Upload an image file to Supabase Storage
 * @param {File} file - The image file to upload
 * @param {string} userId - The authenticated user's ID
 * @returns {Promise<{filePath: string, error: null} | {filePath: null, error: string}>}
 */
export const uploadImage = async (file, userId) => {
  try {
    if (!file) {
      return { filePath: null, error: 'No file provided' };
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes?.includes(file?.type)) {
      return { filePath: null, error: 'Invalid file type. Only JPEG, PNG, WEBP, and GIF are allowed.' };
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file?.size > maxSize) {
      return { filePath: null, error: 'File size exceeds 10MB limit.' };
    }

    // Generate unique file path: userId/timestamp-filename
    const timestamp = Date.now();
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${timestamp}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to Supabase Storage
    const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      return { filePath: null, error: error?.message };
    }

    return { filePath: data?.path, error: null };
  } catch (error) {
    console.error('Upload exception:', error);
    return { filePath: null, error: 'Failed to upload image' };
  }
};

/**
 * Get a signed URL for a private image file
 * @param {string} filePath - The file path in storage
 * @param {number} expiresIn - URL expiry time in seconds (default: 3600 = 1 hour)
 * @returns {Promise<{url: string, error: null} | {url: null, error: string}>}
 */
export const getImageUrl = async (filePath, expiresIn = 3600) => {
  try {
    if (!filePath) {
      return { url: null, error: 'No file path provided' };
    }

    const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.createSignedUrl(filePath, expiresIn);

    if (error) {
      console.error('Get URL error:', error);
      return { url: null, error: error?.message };
    }

    return { url: data?.signedUrl, error: null };
  } catch (error) {
    console.error('Get URL exception:', error);
    return { url: null, error: 'Failed to get image URL' };
  }
};

/**
 * Delete an image file from Supabase Storage
 * @param {string} filePath - The file path in storage
 * @returns {Promise<{success: boolean, error: null} | {success: false, error: string}>}
 */
export const deleteImage = async (filePath) => {
  try {
    if (!filePath) {
      return { success: false, error: 'No file path provided' };
    }

    const { error } = await supabase?.storage?.from(BUCKET_NAME)?.remove([filePath]);

    if (error) {
      console.error('Delete error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete exception:', error);
    return { success: false, error: 'Failed to delete image' };
  }
};

/**
 * List all images for a specific user
 * @param {string} userId - The user's ID
 * @returns {Promise<{files: Array, error: null} | {files: null, error: string}>}
 */
export const listUserImages = async (userId) => {
  try {
    if (!userId) {
      return { files: null, error: 'No user ID provided' };
    }

    const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.list(userId, {
        limit: 100,
        offset: 0,
        sortBy: { column: 'created_at', order: 'desc' }
      });

    if (error) {
      console.error('List files error:', error);
      return { files: null, error: error?.message };
    }

    return { files: data, error: null };
  } catch (error) {
    console.error('List files exception:', error);
    return { files: null, error: 'Failed to list images' };
  }
};